import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PacienteBusca } from '../../../../interfaces/dados-pagamento';
import { AgendaAgendamentoPaciente, AgendaPlanoPaciente } from '../../../../interfaces/agenda';
import { AgendaService } from '../../../../services/agenda/agenda.service';
import { AtendimentoService } from '../../../../services/atendimento/atendimento.service';
import { PaymentService } from '../../../../services/payment/payment.service';
import { DIAS_HISTORICO_ADIANTAMENTOS } from '../../../../utils/pagamento-avulso.util';
import { formatarCpf } from '../agenda-grade/agenda-grade';
import { dataLocal } from '../agenda-filtro';
import { formatarBrl } from '../agenda-agendamento/agenda-agendamento';
import {
  AgendaPacienteEscolhido,
  HISTORICO_MAX_LINHAS,
  HISTORICO_MESES_PASSADO,
  LinhaHistorico,
  janelaHistorico,
  linhaHistorico,
  saldoDisponivel,
} from './agenda-paciente';

const ESPERA_BUSCA_MS = 400;
const MINIMO_BUSCA = 3;

/**
 * Escolha de paciente dos modais da Agenda: busca da Fila (Atendimento/buscar-paciente) e, com
 * `detalhes`, o cartão com plano do cartão, saldo e os últimos agendamentos.
 */
@Component({
  selector: 'app-agenda-paciente',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './agenda-paciente.component.html',
  styleUrl: './agenda-paciente.component.scss',
})
export class AgendaPacienteComponent implements OnInit, OnDestroy {
  @Input() detalhes = true;
  @Input() bloqueado = false;
  /** Já escolhido antes de abrir (ex.: na busca de disponibilidade); emite como uma escolha normal. */
  @Input() inicial: AgendaPacienteEscolhido | null = null;

  /** null = paciente removido (Trocar). */
  @Output() pacienteMudou = new EventEmitter<AgendaPacienteEscolhido | null>();
  /** Plano do cartão (mesma fonte da trava do 224); null = não foi possível saber. */
  @Output() planoCarregado = new EventEmitter<AgendaPlanoPaciente | null>();

  readonly formatarCpf = formatarCpf;
  readonly formatarBrl = formatarBrl;
  readonly mesesHistorico = HISTORICO_MESES_PASSADO;

  busca = '';
  resultados: PacienteBusca[] = [];
  buscando = false;
  buscou = false;
  paciente: AgendaPacienteEscolhido | null = null;
  plano: AgendaPlanoPaciente | null = null;
  carregandoPlano = false;
  saldo = 0;
  historico: AgendaAgendamentoPaciente[] = [];
  carregandoHistorico = false;
  erroHistorico = false;

  private timer: ReturnType<typeof setTimeout> | null = null;
  private seqBusca = 0;
  // Respostas do paciente anterior não podem pintar o cartão do atual.
  private seqPaciente = 0;

  constructor(
    private api: AgendaService,
    private atendimento: AtendimentoService,
    private payment: PaymentService,
  ) {}

  ngOnDestroy() {
    if (this.timer) clearTimeout(this.timer);
    this.seqBusca++;
    this.seqPaciente++;
  }

  get linhasHistorico(): LinhaHistorico[] {
    const hoje = dataLocal(new Date());
    return this.historico.slice(0, HISTORICO_MAX_LINHAS).map(a => linhaHistorico(a, hoje));
  }

  aoDigitar() {
    if (this.timer) clearTimeout(this.timer);
    const termo = this.busca.trim();
    const seq = ++this.seqBusca;
    if (termo.length < MINIMO_BUSCA) {
      this.resultados = [];
      this.buscou = false;
      this.buscando = false;
      return;
    }
    this.timer = setTimeout(async () => {
      this.buscando = true;
      try {
        const r = await this.atendimento.buscarPaciente(termo);
        if (seq === this.seqBusca) { this.resultados = r; this.buscou = true; }
      } finally {
        if (seq === this.seqBusca) this.buscando = false;
      }
    }, ESPERA_BUSCA_MS);
  }

  ngOnInit() {
    if (this.inicial) void this.selecionar({ ...this.inicial });
  }

  escolher(p: PacienteBusca) {
    return this.selecionar({ cdPessoaFisica: String(p.cdPessoaFisica), nome: p.nmPessoaFisica, cpf: p.nrCpf ?? '' });
  }

  private async selecionar(p: AgendaPacienteEscolhido) {
    const seq = ++this.seqPaciente;
    this.paciente = p;
    this.busca = '';
    this.resultados = [];
    this.buscou = false;
    this.limpar();
    this.pacienteMudou.emit(this.paciente);
    const cd = this.paciente.cdPessoaFisica;
    await Promise.all([this.carregarPlano(cd, seq), this.carregarSaldo(cd, seq), this.detalhes ? this.carregarHistorico(cd, seq) : null]);
  }

  trocar() {
    if (this.bloqueado) return;
    this.seqPaciente++;
    this.paciente = null;
    this.limpar();
    this.pacienteMudou.emit(null);
  }

  private limpar() {
    this.plano = null;
    this.carregandoPlano = false;
    this.saldo = 0;
    this.historico = [];
    this.erroHistorico = false;
  }

  private async carregarPlano(cd: string, seq: number) {
    let plano: AgendaPlanoPaciente | null = null;
    this.carregandoPlano = true;
    try {
      plano = await this.api.planoPaciente(cd);
    } catch {
      plano = null;
    }
    if (seq !== this.seqPaciente) return;
    this.carregandoPlano = false;
    this.plano = plano;
    this.planoCarregado.emit(plano);
  }

  private async carregarSaldo(cd: string, seq: number) {
    if (!this.detalhes) return;
    try {
      const lista = await this.payment.getPatientAdvances(cd, DIAS_HISTORICO_ADIANTAMENTOS);
      if (seq === this.seqPaciente) this.saldo = saldoDisponivel(lista ?? [], cd);
    } catch {
      // Sem adiantamento o back responde 404; saldo é só informação.
      if (seq === this.seqPaciente) this.saldo = 0;
    }
  }

  private async carregarHistorico(cd: string, seq: number) {
    this.carregandoHistorico = true;
    try {
      const { dtInicio, dtFim } = janelaHistorico(new Date());
      const lista = await this.api.agendamentosDoPaciente(cd, dtInicio, dtFim);
      if (seq === this.seqPaciente) this.historico = lista;
    } catch {
      // Histórico é apoio: falha vira aviso no painel, nunca trava o agendamento.
      if (seq === this.seqPaciente) this.erroHistorico = true;
    } finally {
      if (seq === this.seqPaciente) this.carregandoHistorico = false;
    }
  }
}
